import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  DollarSign, Check, Clock, AlertCircle, ExternalLink,
  ChevronDown, ChevronUp, Banknote, CreditCard, RefreshCw, Building2,
  Trophy, Flag, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettlementCard = ({ settlement, currentUserId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  
  const isWinner = settlement.winner_id === currentUserId;
  const isPayer = settlement.payer_id === currentUserId;
  const otherParty = isWinner ? settlement.payer_name : settlement.winner_name;
  
  // Format the date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return '';
    }
  };
  
  // Get game type icon and label
  const getGameTypeInfo = () => {
    const type = settlement.event_type;
    if (type === 'skins') {
      return { icon: <Flag className="w-3 h-3" />, label: 'Skins Game' };
    } else if (type === 'stroke_play') {
      return { icon: <Trophy className="w-3 h-3" />, label: 'Stroke Play' };
    }
    return { icon: <DollarSign className="w-3 h-3" />, label: 'Game' };
  };
  
  const gameInfo = getGameTypeInfo();
  
  const getStatusBadge = () => {
    switch (settlement.status) {
      case 'pending':
        return <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-medium">Pending</span>;
      case 'requested_venmo':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium">Venmo Requested</span>;
      case 'requested_zelle':
        return <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">Zelle Requested</span>;
      case 'requested_cash':
        return <span className="px-2 py-1 bg-[var(--ps-success)]/20 text-[var(--ps-success)] rounded-full text-xs font-medium">Cash</span>;
      case 'paid':
        return <span className="px-2 py-1 bg-[var(--ps-success)]/20 text-[var(--ps-success)] rounded-full text-xs font-medium">Paid</span>;
      case 'confirmed':
        return <span className="px-2 py-1 bg-[var(--ps-success)]/30 text-[var(--ps-success)] rounded-full text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Settled</span>;
      default:
        return null;
    }
  };

  const handleRequest = async (method) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/settlements/${settlement.settlement_id}/request`, { method });
      
      if (method === 'venmo' && response.data.venmo_url) {
        // Open Venmo app/link
        window.open(response.data.venmo_url, '_blank');
        toast.success('Opening Venmo...');
      } else if (method === 'cash') {
        toast.success('Marked for cash payment');
      }
      
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payment');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (action) => {
    setLoading(true);
    try {
      await axios.post(`${API}/settlements/${settlement.settlement_id}/confirm`, { action });
      toast.success(action === 'paid' ? 'Marked as paid!' : 'Payment confirmed!');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const openVenmoPayment = () => {
    if (settlement.winner_venmo) {
      const venmoUrl = `venmo://paycharge?txn=pay&recipients=${settlement.winner_venmo}&amount=${settlement.amount}&note=Par%20Seeker%20-%20${encodeURIComponent(settlement.event_name)}`;
      window.open(venmoUrl, '_blank');
    } else {
      toast.error('Winner has not set up Venmo username');
    }
  };

  return (
    <div className={`p-4 rounded-xl border ${
      settlement.status === 'confirmed' ? 'bg-[var(--ps-bg-surface)] border-[var(--ps-success)]' : 
      isWinner ? 'bg-[var(--ps-bg-surface)] border-[var(--ps-success)]' : 'bg-[var(--ps-bg-surface)] border-amber-500/50'
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isWinner ? 'bg-[var(--ps-success)]/20' : 'bg-amber-500/20'
          }`}>
            <DollarSign className={`w-5 h-5 ${isWinner ? 'text-[var(--ps-success)]' : 'text-amber-500'}`} />
          </div>
          <div>
            <p className="font-medium text-[var(--ps-text-primary)]">
              {isWinner ? `Won from ${otherParty}` : `Lost to ${otherParty}`}
            </p>
            <p className="text-2xl font-bold text-[var(--ps-text-primary)]">${settlement.amount.toFixed(2)}</p>
            {/* Game Details */}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                settlement.event_type === 'skins' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
              }`}>
                {gameInfo.icon}
                {gameInfo.label}
              </span>
              <span className="text-xs text-[var(--ps-text-secondary)] font-medium">{settlement.event_name}</span>
              {settlement.has_guest && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                  Guest
                </span>
              )}
            </div>
            {settlement.created_at && (
              <p className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1 mt-0.5">
                <Calendar className="w-3 h-3" />
                {formatDate(settlement.created_at)}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          {getStatusBadge()}
          
          {/* Winner Actions - Request Payment */}
          {isWinner && settlement.status === 'pending' && (
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                onClick={() => handleRequest('venmo')}
                disabled={loading}
                data-testid="request-venmo-btn"
              >
                <CreditCard className="w-4 h-4 mr-1" />
                Venmo
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                onClick={() => handleRequest('zelle')}
                disabled={loading}
                data-testid="request-zelle-btn"
              >
                <Building2 className="w-4 h-4 mr-1" />
                Zelle
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-[var(--ps-success)]/50 text-[var(--ps-success)] hover:bg-[var(--ps-success)]/20"
                onClick={() => handleRequest('cash')}
                disabled={loading}
                data-testid="request-cash-btn"
              >
                <Banknote className="w-4 h-4 mr-1" />
                Cash
              </Button>
            </div>
          )}
          
          {/* Winner Option - Settled in Cash (after Venmo/Zelle was requested) */}
          {isWinner && (settlement.status === 'requested_venmo' || settlement.status === 'requested_zelle') && (
            <Button
              size="sm"
              variant="outline"
              className="border-[var(--ps-success)]/50 text-[var(--ps-success)] hover:bg-[var(--ps-success)]/20"
              onClick={() => handleConfirm('cash_settled')}
              disabled={loading}
              data-testid="settled-cash-btn"
            >
              <Banknote className="w-4 h-4 mr-1" />
              Settled in Cash
            </Button>
          )}
          
          {/* Payer Actions - Pay or Mark as Paid */}
          {isPayer && (settlement.status === 'requested_venmo' || settlement.status === 'requested_zelle' || settlement.status === 'requested_cash') && (
            <div className="flex flex-col items-end gap-2">
              {/* Zelle Info Display */}
              {settlement.status === 'requested_zelle' && settlement.winner_zelle && (
                <div className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-lg">
                  Send to: <span className="font-mono font-bold">{settlement.winner_zelle}</span>
                </div>
              )}
              <div className="flex gap-2">
                {settlement.status === 'requested_venmo' && settlement.winner_venmo && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={openVenmoPayment}
                    data-testid="pay-venmo-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Pay via Venmo
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleConfirm('paid')}
                  disabled={loading}
                  data-testid="mark-paid-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  I Paid
                </Button>
              </div>
            </div>
          )}
          
          {/* Winner Confirm Receipt */}
          {isWinner && settlement.status === 'paid' && (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleConfirm('confirmed')}
              disabled={loading}
              data-testid="confirm-received-btn"
            >
              <Check className="w-4 h-4 mr-1" />
              Confirm Received
            </Button>
          )}
          
          {/* Payer Actions - Direct payment options when pending */}
          {isPayer && settlement.status === 'pending' && (
            <div className="flex flex-col items-end gap-2">
              <p className="text-xs text-stone-500">Pay now:</p>
              <div className="flex flex-wrap gap-2 justify-end">
                {settlement.winner_venmo && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={openVenmoPayment}
                    disabled={loading}
                    data-testid="pay-venmo-direct-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Venmo
                  </Button>
                )}
                {settlement.winner_zelle && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={() => {
                      navigator.clipboard.writeText(settlement.winner_zelle);
                      toast.success(`Zelle: ${settlement.winner_zelle} copied!`);
                    }}
                    disabled={loading}
                    data-testid="copy-zelle-btn"
                  >
                    <Building2 className="w-4 h-4 mr-1" />
                    Zelle
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-300 text-green-700 hover:bg-green-50"
                  onClick={() => handleConfirm('paid')}
                  disabled={loading}
                  data-testid="mark-paid-pending-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  I Paid
                </Button>
              </div>
              {!settlement.winner_venmo && !settlement.winner_zelle && (
                <p className="text-xs text-stone-400">Winner hasn't set up payment info</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GameSettlements = ({ gameType, eventId, currentUserId }) => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const fetchSettlements = async () => {
    try {
      const response = await axios.get(`${API}/settlements/game/${gameType}/${eventId}`);
      setSettlements(response.data.settlements || []);
    } catch (error) {
      console.error('Error fetching settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, [gameType, eventId]);

  if (loading) {
    return (
      <Card className="border-green-200">
        <CardContent className="py-6 text-center">
          <div className="spinner w-6 h-6 border-green-600 border-t-transparent mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (settlements.length === 0) {
    return null; // No settlements to show
  }

  const mySettlements = settlements.filter(
    s => s.winner_id === currentUserId || s.payer_id === currentUserId
  );
  const pendingCount = mySettlements.filter(s => s.status !== 'confirmed').length;

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
      <CardHeader className="pb-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between"
          data-testid="toggle-settlements-btn"
        >
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Settlements
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                {pendingCount} pending
              </span>
            )}
          </CardTitle>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-3">
          {mySettlements.map(settlement => (
            <SettlementCard
              key={settlement.settlement_id}
              settlement={settlement}
              currentUserId={currentUserId}
              onUpdate={fetchSettlements}
            />
          ))}
          
          {mySettlements.length === 0 && (
            <p className="text-center text-stone-500 py-4">No settlements involving you</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Standalone page for viewing all settlements
const MySettlementsPage = ({ currentUserId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'owed_to_me', 'i_owe', 'completed'

  const fetchSettlements = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const response = await axios.get(`${API}/settlements/my-settlements`);
      setData(response.data);
      if (showToast) toast.success('Settlements refreshed');
    } catch (error) {
      console.error('Error fetching settlements:', error);
      toast.error('Failed to load settlements');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="dark-spinner w-8 h-8" />
      </div>
    );
  }

  // Calculate counts
  const owedToMeCount = data?.owed_to_me?.length || 0;
  const iOweCount = data?.i_owe?.length || 0;
  const completedCount = data?.completed?.length || 0;
  const totalCount = owedToMeCount + iOweCount + completedCount;

  return (
    <div className="dark-page space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--ps-text-primary)]">My Settlements</h2>
          <p className="text-sm text-[var(--ps-text-secondary)]">Track money owed and paid</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchSettlements(true)}
          disabled={refreshing}
          className="dark-btn-secondary"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards - Dark Theme Clickable Filters */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setActiveFilter(activeFilter === 'owed_to_me' ? 'all' : 'owed_to_me')}
          className={`text-left transition-all rounded-xl ${activeFilter === 'owed_to_me' ? 'ring-2 ring-[var(--ps-success)] ring-offset-2 ring-offset-[var(--ps-bg-base)] scale-[1.02]' : ''}`}
          data-testid="filter-owed-to-me"
        >
          <div className={`dark-card h-full p-4 text-center border-2 ${activeFilter === 'owed_to_me' ? 'border-[var(--ps-success)]' : 'border-[var(--ps-success)]/30'}`}>
            <p className="text-sm text-[var(--ps-success)] font-medium">Won</p>
            <p className="text-2xl font-bold text-[var(--ps-success)]">${(data?.total_owed_to_me || 0).toFixed(2)}</p>
            {owedToMeCount > 0 && (
              <p className="text-xs text-[var(--ps-text-muted)] mt-1">{owedToMeCount} game{owedToMeCount !== 1 ? 's' : ''}</p>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveFilter(activeFilter === 'i_owe' ? 'all' : 'i_owe')}
          className={`text-left transition-all rounded-xl ${activeFilter === 'i_owe' ? 'ring-2 ring-[var(--ps-warning)] ring-offset-2 ring-offset-[var(--ps-bg-base)] scale-[1.02]' : ''}`}
          data-testid="filter-i-owe"
        >
          <div className={`dark-card h-full p-4 text-center border-2 ${activeFilter === 'i_owe' ? 'border-[var(--ps-warning)]' : 'border-[var(--ps-warning)]/30'}`}>
            <p className="text-sm text-[var(--ps-warning)] font-medium">Lost</p>
            <p className="text-2xl font-bold text-[var(--ps-warning)]">${(data?.total_i_owe || 0).toFixed(2)}</p>
            {iOweCount > 0 && (
              <p className="text-xs text-[var(--ps-text-muted)] mt-1">{iOweCount} game{iOweCount !== 1 ? 's' : ''}</p>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveFilter(activeFilter === 'completed' ? 'all' : 'completed')}
          className={`text-left transition-all rounded-xl ${activeFilter === 'completed' ? 'ring-2 ring-[var(--ps-text-muted)] ring-offset-2 ring-offset-[var(--ps-bg-base)] scale-[1.02]' : ''}`}
          data-testid="filter-net-balance"
        >
          <div className={`dark-card h-full p-4 text-center border-2 ${
            (data?.net_balance || 0) >= 0 
              ? (activeFilter === 'completed' ? 'border-[var(--ps-success)]' : 'border-[var(--ps-success)]/30')
              : (activeFilter === 'completed' ? 'border-[var(--ps-error)]' : 'border-[var(--ps-error)]/30')
          }`}>
            <p className={`text-sm font-medium ${(data?.net_balance || 0) >= 0 ? 'text-[var(--ps-success)]' : 'text-[var(--ps-error)]'}`}>Total</p>
            <p className={`text-2xl font-bold ${(data?.net_balance || 0) >= 0 ? 'text-[var(--ps-success)]' : 'text-[var(--ps-error)]'}`}>
              {(data?.net_balance || 0) >= 0 ? '+' : ''}${(data?.net_balance || 0).toFixed(2)}
            </p>
            {completedCount > 0 && (
              <p className="text-xs text-[var(--ps-text-muted)] mt-1">{completedCount} settled</p>
            )}
          </div>
        </button>
      </div>

      {/* Filter indicator */}
      {activeFilter !== 'all' && (
        <div className="flex items-center justify-between bg-[var(--ps-bg-elevated)] rounded-lg px-3 py-2">
          <p className="text-sm text-[var(--ps-text-secondary)]">
            Showing: <span className="font-medium text-[var(--ps-text-primary)]">
              {activeFilter === 'owed_to_me' ? 'Won' : 
               activeFilter === 'i_owe' ? 'Lost' : 'Settled'}
            </span>
          </p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveFilter('all')}
            className="text-xs h-7 text-[var(--ps-text-secondary)]"
          >
            Show All
          </Button>
        </div>
      )}

      {/* Won (Owed to Me) */}
      {(activeFilter === 'all' || activeFilter === 'owed_to_me') && data?.owed_to_me?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-bold text-[var(--ps-text-primary)] flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[var(--ps-success)]" />
            Won ({data.owed_to_me.length})
          </h3>
          {data.owed_to_me.map(settlement => (
            <SettlementCard
              key={settlement.settlement_id}
              settlement={settlement}
              currentUserId={currentUserId}
              onUpdate={fetchSettlements}
            />
          ))}
        </div>
      )}

      {/* Lost (I Owe) */}
      {(activeFilter === 'all' || activeFilter === 'i_owe') && data?.i_owe?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-bold text-[var(--ps-text-primary)] flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[var(--ps-warning)]" />
            Lost ({data.i_owe.length})
          </h3>
          {data.i_owe.map(settlement => (
            <SettlementCard
              key={settlement.settlement_id}
              settlement={settlement}
              currentUserId={currentUserId}
              onUpdate={fetchSettlements}
            />
          ))}
        </div>
      )}

      {/* Completed */}
      {(activeFilter === 'all' || activeFilter === 'completed') && data?.completed?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-bold text-[var(--ps-text-secondary)] flex items-center gap-2">
            <Check className="w-5 h-5" />
            Settled ({data.completed.length})
          </h3>
          {data.completed.map(settlement => (
            <SettlementCard
              key={settlement.settlement_id}
              settlement={settlement}
              currentUserId={currentUserId}
              onUpdate={fetchSettlements}
            />
          ))}
        </div>
      )}

      {/* Empty State - when filter shows nothing */}
      {activeFilter !== 'all' && (
        (activeFilter === 'owed_to_me' && !data?.owed_to_me?.length) ||
        (activeFilter === 'i_owe' && !data?.i_owe?.length) ||
        (activeFilter === 'completed' && !data?.completed?.length)
      ) && (
        <div className="dark-card p-8 text-center border-dashed border-2 border-[var(--ps-border)]">
          <p className="text-[var(--ps-text-secondary)]">No settlements in this category</p>
        </div>
      )}

      {/* Empty State - no settlements at all */}
      {activeFilter === 'all' && !data?.owed_to_me?.length && !data?.i_owe?.length && !data?.completed?.length && (
        <div className="dark-card p-12 text-center border-dashed border-2 border-[var(--ps-border)]">
          <DollarSign className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">No Settlements</h3>
          <p className="text-[var(--ps-text-secondary)] text-sm">
            Play games with entry fees to see settlements here
          </p>
        </div>
      )}
    </div>
  );
};

export { GameSettlements, MySettlementsPage, SettlementCard };
